import AutocompleteField from 'components/AutocomleteField';
import renderField from 'components/Field/reduxForm';

import renderSelect from 'components/Select/reduxForm';
import { Map } from 'immutable';
import { findKey, forEach, get, isEqual, isObject, isString, omit, pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Field } from 'redux-form/immutable';
import * as inputFilters from 'utils/reduxForm/inputFilters';
import formFields, { hierarchyLevels, responseFieldsMap } from './constants/formFields';
import houseTypeMap from './constants/houseTypeMap';
import houseTypeTitleMap from './constants/houseTypeTitleMap';
import structTypeMap from './constants/structTypeMap';
import structTypeTitleMap from './constants/structTypeTitleMap';
import './styles.scss';
import getOptionsFromObject from './utils/getOptionsFromObject';
import { checkExistingTaxAuthority } from '../../modules/Taps/services/API';
import priorities from './constants/taxInspectionPriority';

const houseTypeOptions = getOptionsFromObject(houseTypeTitleMap);
const structTypeOptions = getOptionsFromObject(structTypeTitleMap);

const COMMON_FIELDS_FOR_CLEAR = [
  'houseNumber',
  'buildingNumber',
  'structNumber',
  'apartmentNumber',
];

export default class FIAS extends Component {
  static propTypes = {
    autofill: PropTypes.func.isRequired,
    parentName: PropTypes.string,
    initialValues: PropTypes.instanceOf(Map),
    formFields: PropTypes.object,
    isDisabledForm: PropTypes.bool,
    taxAuthorityParams: PropTypes.shape({
      name: PropTypes.string,
      insideFiasSection: PropTypes.number,
    }),
  };

  static defaultProps = {
    needAutofill: true,
    parentName: null,
    formFields: formFields,
    taxAuthorityParams: null,
  };

  state = {
    selectedRegion: null,
    selectedDistrict: null,
    selectedCity: null,
    selectedLocality: null,
    selectedStreet: null,
    selectedHouseType: houseTypeMap.house,
    selectedHouseNumber: null,
    selectedStructType: structTypeMap.structure,
    selectedStructNumber: null,
    postCodeAutofilled: false,
    selectedId: null,
  };

  constructor(props) {
    super(props);
    this.FIELDS_FOR_CLEAR = {
      REGION_CHANGED: pick(this.props.formFields, [
        'postCode',
        'district',
        'city',
        'locality',
        'street',
        ...COMMON_FIELDS_FOR_CLEAR,
      ]),
      DISTRICT_CHANGED: pick(this.props.formFields, [
        'locality',
        'street',
        ...COMMON_FIELDS_FOR_CLEAR,
      ]),
      CITY_CHANGED: pick(this.props.formFields, ['locality', 'street', ...COMMON_FIELDS_FOR_CLEAR]),
      LOCALITY_CHANGED: pick(this.props.formFields, ['street', ...COMMON_FIELDS_FOR_CLEAR]),
      STREET_CHANGED: pick(this.props.formFields, COMMON_FIELDS_FOR_CLEAR),
      HOUSE_CHANGED: pick(this.props.formFields, ['buildingNumber']),
      STRUCT_CHANGED: pick(this.props.formFields, ['apartmentNumber']),
    };
  }

  componentDidMount() {
    this.fillInitialValues();
  }

  fillInitialValues = () => {
    const { autofill, initialValues, formFields } = this.props;
    const houseType =
      initialValues && formFields.houseType ? initialValues.get(formFields.houseType.name) : null;
    const structType =
      initialValues && formFields.structType ? initialValues.get(formFields.structType.name) : null;

    formFields.houseType &&
      autofill(this.getFieldName(formFields.houseType.name), houseType || houseTypeMap.house);
    formFields.structType &&
      autofill(
        this.getFieldName(formFields.structType.name),
        structType || structTypeMap.structure,
      );

    if (!initialValues) {
      return;
    }

    const region = initialValues.get(formFields.region.name) || null;
    const district = initialValues.get(formFields.district.name) || null;
    const city = initialValues.get(formFields.city.name) || null;
    const locality = initialValues.get(formFields.locality.name) || null;
    const street = initialValues.get(formFields.street.name) || null;
    const houseNumber = initialValues.get(formFields.houseNumber.name) || null;
    const selectedId = initialValues.get(formFields.addressObjectId.name) || null;

    this.setState({
      selectedRegion: region,
      selectedDistrict: district,
      selectedCity: city,
      selectedLocality: locality,
      selectedStreet: street,
      selectedHouseType: houseType || houseTypeMap.house,
      selectedHouseNumber: houseNumber,
      selectedStructType: structType || structTypeMap.structure,
      selectedId,
    });
  };

  getFieldName = (name) => {
    const { parentName } = this.props;
    return parentName ? `${parentName}.${name}` : name;
  };

  getTaxNameByField = (fieldName) => {
    return `taxCode.${fieldName}`;
  };

  setTaxAuthority = async (taxAuthority, bindFieldName) => {
    const {
      autofill,
      taxAuthorityParams: { insideFiasSection, name },
    } = this.props;

    const fieldName = insideFiasSection ? this.getFieldName(name) : name;
    if (!taxAuthority) {
      autofill(fieldName, null);
      autofill('setFromAddress', false);
      return;
    }

    const response = await checkExistingTaxAuthority(taxAuthority);
    if (response.isOk && response.data) {
      const stateName = this.getTaxNameByField(bindFieldName);
      const { code: value, name: label } = response.data;
      this.setState({ [stateName]: { label, value } });
      autofill(fieldName, { label, value });
      autofill(`setFromAddress`, true);
    } else {
      autofill(fieldName, null);
      autofill('setFromAddress', false);
    }
  };

  findTaxAuthorityByPriority = (currentPriority) => {
    const next = currentPriority - 1;
    if (next < 0) {
      return null;
    }
    const key = findKey(priorities, (val) => val === next);
    const name = this.getTaxNameByField(key);
    if (!this.state[name]) {
      return this.findTaxAuthorityByPriority(next);
    }
    return this.state[name];
  };

  getPreviousTaxAuthority = (bindFieldName) => {
    const currentPriority = priorities[bindFieldName];
    return this.findTaxAuthorityByPriority(currentPriority);
  };

  resetTaxAuthorityByPriority = (bindFieldName) => {
    const { autofill, taxAuthorityParams } = this.props;
    if (!taxAuthorityParams) {
      return;
    }
    const { insideFiasSection, name } = taxAuthorityParams;
    const fieldName = insideFiasSection ? this.getFieldName(name) : name;
    const prevTaxAuthority = this.getPreviousTaxAuthority(bindFieldName);
    autofill(fieldName, prevTaxAuthority);
    autofill(`setFromAddress`, true);
  };

  handleChangeAutoCompleteField = (value, bindFieldName) => {
    if (!value) {
      this.resetTaxAuthorityByPriority(bindFieldName);
    }
  };

  changeAddressId = (id) => {
    const { autofill, formFields } = this.props;
    autofill(this.getFieldName(formFields.addressObjectId.name), id);
  };

  onSelectBuildingNumber = (value) => {
    const { taxAuthorityParams } = this.props;
    this.setState(
      {
        selectedBuildingNumber: value,
      },
      () => {
        if (value && value.postCode) {
          this.updatePostCode(value.postCode);
        }
        taxAuthorityParams &&
          this.setTaxAuthority(value.taxInspectionCode, formFields.buildingNumber.name);
      },
    );
  };

  onSelectStruct = (value) => {
    const { taxAuthorityParams } = this.props;
    this.setState(
      {
        selectedStructNumber: value,
      },
      () => {
        this.clearFields(this.FIELDS_FOR_CLEAR.STRUCT_CHANGED);
        if (value && value.postCode) {
          this.updatePostCode(value.postCode);
        }
        taxAuthorityParams &&
          this.setTaxAuthority(value.taxInspectionCode, formFields.structNumber.name);
      },
    );
  };

  onSelectHouse = (value) => {
    const { taxAuthorityParams } = this.props;
    this.setState(
      {
        selectedHouseNumber: value,
      },
      () => {
        this.clearFields(this.FIELDS_FOR_CLEAR.BUILDING_NUMBER);
        if (value && value.postCode) {
          this.updatePostCode(value.postCode);
        }
        taxAuthorityParams &&
          this.setTaxAuthority(value.taxInspectionCode, formFields.houseNumber.name);
      },
    );
  };

  onSelectStreet = (value) => {
    const { taxAuthorityParams } = this.props;
    const additionalFields =
      isObject(value) && value.additionalFields ? value.additionalFields : null;
    const city =
      additionalFields && additionalFields.city ? additionalFields.city : this.state.selectedCity;
    const locality =
      additionalFields && additionalFields.locality
        ? additionalFields.locality
        : this.state.selectedLocality;
    this.setState(
      {
        selectedCity: city,
        selectedLocality: locality,
        selectedStreet: value,
        selectedHouseNumber: null,
        selectedStructNumber: null,
        selectedId: value ? value.id : this.state.selectedId,
      },
      () => {
        isObject(value) && this.fillAdditionalFields(value.additionalFields);
        this.clearFields(this.FIELDS_FOR_CLEAR.STREET_CHANGED);
        if (!value) {
          return;
        }

        this.changeAddressId(value.id);
        if (value.postCode) {
          this.updatePostCode(value.postCode);
        }
        taxAuthorityParams && this.setTaxAuthority(value.taxInspectionCode, formFields.street.name);
      },
    );
  };

  onSelectLocality = (value) => {
    const { taxAuthorityParams } = this.props;
    const additionalFields =
      isObject(value) && value.additionalFields ? value.additionalFields : null;
    const region =
      additionalFields && additionalFields.region
        ? additionalFields.region
        : this.state.selectedRegion;
    const district =
      additionalFields && additionalFields.district
        ? additionalFields.district
        : this.state.selectedDistrict;
    const city =
      additionalFields && additionalFields.city ? additionalFields.city : this.state.selectedCity;
    this.setState(
      {
        selectedRegion: region,
        selectedDistrict: district,
        selectedCity: city,
        selectedLocality: value,
        selectedStreet: this.state.selectedCity ? this.state.selectedStreet : null,
        selectedHouseNumber: null,
        selectedStructNumber: null,
        selectedId: value ? value.id : this.state.selectedId,
      },
      () => {
        isObject(value) && this.fillAdditionalFields(value.additionalFields);
        const fields = this.state.selectedCity
          ? omit(this.FIELDS_FOR_CLEAR.LOCALITY_CHANGED, ['street'])
          : this.FIELDS_FOR_CLEAR.LOCALITY_CHANGED;
        this.clearFields(fields);

        if (!value) {
          return;
        }

        this.changeAddressId(value.id);

        if (value && value.postCode) {
          this.updatePostCode(value.postCode);
        }
        taxAuthorityParams &&
          this.setTaxAuthority(value.taxInspectionCode, formFields.locality.name);
      },
    );
  };

  onSelectDistrict = (value) => {
    const { taxAuthorityParams } = this.props;
    this.setState(
      {
        selectedDistrict: value,
        selectedLocality: this.state.selectedCity ? this.state.selectedLocality : null,
        selectedStreet: null,
        selectedHouseNumber: null,
        selectedStructNumber: null,
        selectedId: value ? value.id : this.state.selectedId,
      },
      () => {
        const fields = this.state.selectedCity
          ? omit(this.FIELDS_FOR_CLEAR.LOCALITY_CHANGED, ['locality'])
          : this.FIELDS_FOR_CLEAR.DISTRICT_CHANGED;
        this.clearFields(fields);
        if (!value) {
          return;
        }
        taxAuthorityParams &&
          this.setTaxAuthority(value.taxInspectionCode, formFields.district.name);
        this.changeAddressId(value.id);
      },
    );
  };

  onSelectCity = (value) => {
    const { autofill, formFields, taxAuthorityParams } = this.props;
    const additionalFields =
      isObject(value) && value.additionalFields ? value.additionalFields : null;
    const region =
      additionalFields && additionalFields.region
        ? additionalFields.region
        : this.state.selectedRegion;
    const district =
      additionalFields && additionalFields.district
        ? additionalFields.district
        : this.state.selectedDistrict;
    this.setState(
      {
        selectedRegion: region,
        selectedDistrict: district,
        selectedCity: value,
        selectedLocality: this.state.selectedDistrict ? this.state.selectedLocality : null,
        selectedStreet: this.state.selectedLocality ? this.state.selectedStreet : null,
        selectedHouseNumber: null,
        selectedStructNumber: null,
        selectedId: value ? value.id : this.state.selectedId,
      },
      () => {
        isObject(value) && this.fillAdditionalFields(value.additionalFields);
        let fields = this.state.selectedDistrict
          ? omit(this.FIELDS_FOR_CLEAR.CITY_CHANGED, ['locality'])
          : this.FIELDS_FOR_CLEAR.CITY_CHANGED;

        fields = this.state.selectedLocality ? omit(fields, ['street']) : fields;
        this.clearFields(fields);

        if (!value) {
          return;
        }
        this.changeAddressId(value.id);

        if (value.postCode) {
          this.updatePostCode(value.postCode);
        }

        if (value.regionCode) {
          autofill(this.getFieldName(formFields.regionCode.name), value.regionCode);
        }
        taxAuthorityParams && this.setTaxAuthority(value.taxInspectionCode, formFields.city.name);
      },
    );
  };

  fillAdditionalFields = (fields) => {
    const { autofill } = this.props;
    if (!fields) {
      return;
    }

    forEach(fields, (value, field) => {
      if (!responseFieldsMap[field]) {
        return;
      }

      autofill(this.getFieldName(responseFieldsMap[field].name), value);
    });
  };

  onSelectRegion = (value) => {
    const { autofill, formFields, taxAuthorityParams } = this.props;
    const isChanged = !isEqual(this.state.selectedRegion, value);
    this.setState(
      {
        postCodeAutofilled: isChanged ? false : this.state.postCodeAutofilled,
        selectedRegion: value,
        selectedDistrict: null,
        selectedCity: null,
        selectedLocality: null,
        selectedStreet: null,
        selectedHouseNumber: null,
        selectedStructNumber: null,
        selectedId: value ? value.id : this.state.selectedId,
      },
      () => {
        if (!isChanged) {
          return;
        }

        this.clearFields(this.FIELDS_FOR_CLEAR.REGION_CHANGED);
        if (!value) {
          autofill(this.getFieldName(formFields.regionCode.name), null);
          return;
        }
        taxAuthorityParams && this.setTaxAuthority(value.taxInspectionCode, formFields.region.name);
        this.changeAddressId(value.id);
        autofill(this.getFieldName(formFields.regionCode.name), value.regionCode);
      },
    );
  };

  onChangePostCode = () => {
    this.setState({ postCodeAutofilled: false });
  };

  returnPrevTaxCodeValueIfEmpty = (value) => {
    const { autofill, taxAuthorityParams } = this.props;
    if (!value && taxAuthorityParams) {
      const { insideFiasSection, name } = taxAuthorityParams;

      const fieldName = insideFiasSection ? this.getFieldName(name) : name;
      let option;
      for (const optionName of hierarchyLevels) {
        const taxCodeOption = get(this.state, `taxCode.${optionName}`);
        if (taxCodeOption) {
          option = taxCodeOption;
          break;
        }
      }
      autofill(fieldName, option);
      autofill('setFromAddress', !!option);
    }
  };

  updatePostCode = (value) => {
    if (!value) {
      return;
    }

    const { autofill, formFields } = this.props;
    autofill(this.getFieldName(formFields.postCode.name), value);
    this.setState({ postCodeAutofilled: true });
  };

  clearFields = (fields) => {
    const { autofill } = this.props;
    forEach(fields, (field) => {
      autofill(this.getFieldName(field.name), null);
    });
  };
  renderDistrict = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedRegion } = this.state;
    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;
    const requestArgs = {
      region: region || null,
    };

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.district.name)}
        key={this.getFieldName(formFields.district.name)}
        label={formFields.district.label}
        getOptions={formFields.district.optionsGetter}
        requestArgs={requestArgs}
        autofill={autofill}
        isDisabled={!region || !!isDisabledForm}
        onSelect={(value) => this.onSelectDistrict(value)}
        onChange={(value) => this.handleChangeAutoCompleteField(value, formFields.district.name)}
        tooltipContent={formFields.district.tooltipContent}
        tooltipTitle={formFields.district.tooltipTitle}
      />
    );
  };
  renderCity = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedRegion, selectedDistrict } = this.state;

    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;
    const district = isObject(selectedDistrict) ? selectedDistrict.value : selectedDistrict;
    const requestArgs = {
      region: region || null,
      district: district || null,
    };

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.city.name)}
        key={this.getFieldName(formFields.city.name)}
        label={formFields.city.label}
        getOptions={formFields.city.optionsGetter}
        requestArgs={requestArgs}
        autofill={autofill}
        onSelect={(value) => this.onSelectCity(value)}
        onChange={(value) => this.handleChangeAutoCompleteField(value, formFields.city.name)}
        tooltipContent={formFields.city.tooltipContent}
        isDisabled={!region || !!isDisabledForm}
        tooltipTitle={formFields.city.tooltipTitle}
      />
    );
  };
  renderLocality = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedRegion, selectedDistrict, selectedCity } = this.state;

    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;
    const district = isObject(selectedDistrict) ? selectedDistrict.value : selectedDistrict;
    const requestArgs = {
      region: region || null,
      district: district || null,
      city: selectedCity ? selectedCity.value : null,
    };

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.locality.name)}
        key={this.getFieldName(formFields.locality.name)}
        label={formFields.locality.label}
        getOptions={formFields.locality.optionsGetter}
        requestArgs={requestArgs}
        autofill={autofill}
        onSelect={(value) => this.onSelectLocality(value)}
        onChange={(value) => this.handleChangeAutoCompleteField(value, formFields.locality.name)}
        tooltipContent={formFields.locality.tooltipContent}
        tooltipTitle={formFields.locality.tooltipTitle}
        isDisabled={!region || !!isDisabledForm}
      />
    );
  };

  renderStreet = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedRegion, selectedLocality, selectedCity, selectedDistrict } = this.state;

    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;
    const district = isObject(selectedDistrict) ? selectedDistrict.value : selectedDistrict;
    const requestArgs = {
      region: region || null,
      locality: selectedLocality ? selectedLocality.value : null,
      district: district || null,
      city: selectedCity ? selectedCity.value : null,
    };

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.street.name)}
        key={this.getFieldName(formFields.street.name)}
        label={formFields.street.label}
        getOptions={formFields.street.optionsGetter}
        requestArgs={requestArgs}
        selectable={formFields.street.selectable}
        maxLength={formFields.street.maxLength}
        autofill={autofill}
        onSelect={(value) => this.onSelectStreet(value)}
        onChange={(value) => this.handleChangeAutoCompleteField(value, formFields.street.name)}
        tooltipContent={formFields.street.tooltipContent}
        tooltipTitle={formFields.street.tooltipTitle}
        isDisabled={!region || !!isDisabledForm}
      />
    );
  };

  renderHouseNumber = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedId, selectedHouseType, selectedRegion } = this.state;
    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;

    let requestArgs = {
      parentId: selectedId,
    };

    if (selectedHouseType) {
      requestArgs.houseType = isString(selectedHouseType)
        ? selectedHouseType
        : selectedHouseType.value;
    }

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.houseNumber.name)}
        key={this.getFieldName(formFields.houseNumber.name)}
        getOptions={formFields.houseNumber.optionsGetter}
        requestArgs={requestArgs}
        selectable={formFields.houseNumber.selectable}
        autofill={autofill}
        valueMinLength={formFields.houseNumber.valueMinLength}
        onSelect={(value) => this.onSelectHouse(value)}
        onChange={this.returnPrevTaxCodeValueIfEmpty}
        tooltipContent={formFields.houseNumber.tooltipContent}
        tooltipTitle={formFields.houseNumber.tooltipTitle}
        isDisabled={!region || !!isDisabledForm}
        label={!formFields.houseType ? formFields.houseNumber.label : ''}
        hasHelpWrap={false}
      />
    );
  };

  renderBuildingNumber = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedId, selectedHouseType, selectedHouseNumber, selectedRegion } = this.state;
    const region = isObject(selectedRegion) ? selectedRegion.value : selectedRegion;

    let requestArgs = {
      parentId: selectedId,
    };

    if (selectedHouseType && selectedHouseNumber) {
      requestArgs.houseType = isString(selectedHouseType)
        ? selectedHouseType
        : selectedHouseType.value;
      requestArgs.houseNumber = selectedHouseNumber.value;
    }

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.buildingNumber.name)}
        key={this.getFieldName(formFields.buildingNumber.name)}
        label={formFields.buildingNumber.label}
        getOptions={formFields.buildingNumber.optionsGetter}
        requestArgs={requestArgs}
        selectable={formFields.buildingNumber.selectable}
        autofill={autofill}
        valueMinLength={formFields.buildingNumber.valueMinLength}
        onSelect={(value) => this.onSelectBuildingNumber(value)}
        tooltipContent={formFields.buildingNumber.tooltipContent}
        tooltipTitle={formFields.buildingNumber.tooltipTitle}
        isDisabled={!region || !!isDisabledForm}
        hasHelpWrap={false}
        onChange={this.returnPrevTaxCodeValueIfEmpty}
      />
    );
  };

  renderStructNumber = () => {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { selectedId, selectedStructType, selectedHouseType, selectedHouseNumber } = this.state;

    let requestArgs = {
      parentId: selectedId,
    };

    if (selectedStructType) {
      requestArgs.structType = isString(selectedStructType)
        ? selectedStructType
        : selectedStructType.value;
    }

    if (selectedHouseType && selectedHouseNumber) {
      requestArgs.houseType = isString(selectedHouseType)
        ? selectedHouseType
        : selectedHouseType.value;
      requestArgs.houseNumber = selectedHouseNumber.value;
    }

    return (
      <AutocompleteField
        name={this.getFieldName(formFields.structNumber.name)}
        key={this.getFieldName(formFields.structNumber.name)}
        getOptions={formFields.structNumber.optionsGetter}
        requestArgs={requestArgs}
        selectable={formFields.structNumber.selectable}
        autofill={autofill}
        valueMinLength={formFields.structNumber.valueMinLength}
        isDisabled={!selectedId || !!isDisabledForm}
        hasHelpWrap={false}
        onSelect={(value) => this.onSelectStruct(value)}
        onChange={this.returnPrevTaxCodeValueIfEmpty}
      />
    );
  };

  renderHidedFields = () => {
    const { formFields } = this.props;
    return (
      <div>
        <Field
          className="d-none"
          name={this.getFieldName(formFields.regionCode.name)}
          key={this.getFieldName(formFields.regionCode.name)}
          component={renderField}
          type="hidden"
        />
        <Field
          className="d-none"
          name={this.getFieldName(formFields.addressObjectId.name)}
          key={this.getFieldName(formFields.addressObjectId.name)}
          component={renderField}
          type="hidden"
        />
      </div>
    );
  };

  render() {
    const { autofill, isDisabledForm, formFields } = this.props;
    const { postCodeAutofilled } = this.state;
    let postCodeValidation = {};

    if (postCodeAutofilled) {
      postCodeValidation.isValid = true;
      postCodeValidation.validationMessage =
        'Значение изменено в соответствии с адресной информацией';
    }

    return (
      <div className="cRow">
        <div className="cCol cCol__50">
          <Field
            name={this.getFieldName(formFields.postCode.name)}
            key={this.getFieldName(formFields.postCode.name)}
            label={formFields.postCode.label}
            maxLength={formFields.postCode.maxLength}
            component={renderField}
            normalize={inputFilters.numberOnly}
            tooltipContent={formFields.postCode.tooltipContent}
            tooltipTitle={formFields.postCode.tooltipTitle}
            onChange={this.onChangePostCode}
            isDisabled={!!isDisabledForm}
            validate={!isDisabledForm && formFields.postCode.validate}
            {...postCodeValidation}
          />
          <AutocompleteField
            name={this.getFieldName(formFields.region.name)}
            key={this.getFieldName(formFields.region.name)}
            label={formFields.region.label}
            getOptions={formFields.region.optionsGetter}
            isRequired={formFields.region.isRequired}
            validate={!isDisabledForm && formFields.region.validate}
            autofill={autofill}
            isDisabled={!!isDisabledForm}
            tooltipContent={formFields.region.tooltipContent}
            valueMinLength={formFields.region.valueMinLength}
            tooltipTitle={formFields.region.tooltipTitle}
            onSelect={(value) => this.onSelectRegion(value)}
            onChange={(value) => this.handleChangeAutoCompleteField(value, formFields.region.name)}
          />
          {this.renderDistrict()}
          {this.renderCity()}
        </div>
        <div className="cCol cCol__50">
          {this.renderLocality()}

          {this.renderStreet()}
          {this.renderHidedFields()}
          {formFields.houseType ? (
            <>
              <div className="cRow">
                <div className="cCol cCol__30 withoutLabel">
                  <Field
                    name={this.getFieldName(formFields.houseType.name)}
                    key={this.getFieldName(formFields.houseType.name)}
                    component={renderSelect}
                    isFull={true}
                    parse={(val) => val.value}
                    options={houseTypeOptions}
                    withoutHelpWrapper={true}
                    isDisabled={!!isDisabledForm}
                    onChange={(e, value) => this.setState({ selectedHouseType: value })}
                  />
                </div>
                <div className="cCol cCol__30 withoutLabel">{this.renderHouseNumber()}</div>
                <div className="cCol cCol__30">
                  <Field
                    name={this.getFieldName(formFields.apartmentNumber.name)}
                    key={this.getFieldName(formFields.apartmentNumber.name)}
                    label={formFields.apartmentNumber.label}
                    component={renderField}
                    isDisabled={!!isDisabledForm}
                    onChange={(e, value) => this.setState({ selectedApartmentNumber: value })}
                    tooltipContent={formFields.apartmentNumber.tooltipContent}
                    tooltipTitle={formFields.apartmentNumber.tooltipTitle}
                    hasHelpWrap={false}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="cRow">
              <div className="cCol cCol__30">{this.renderHouseNumber()}</div>
              <div className="cCol cCol__30">{this.renderBuildingNumber()}</div>
              <div className="cCol cCol__30">
                <Field
                  name={this.getFieldName(formFields.apartmentNumber.name)}
                  key={this.getFieldName(formFields.apartmentNumber.name)}
                  label={formFields.apartmentNumber.label}
                  component={renderField}
                  isDisabled={!!isDisabledForm}
                  onChange={(e, value) => this.setState({ selectedApartmentNumber: value })}
                  tooltipContent={formFields.apartmentNumber.tooltipContent}
                  tooltipTitle={formFields.apartmentNumber.tooltipTitle}
                />
              </div>
            </div>
          )}

          {formFields.structType && (
            <>
              <div className="cRow">
                <div className="cCol cCol__30 withoutLabel">
                  <Field
                    name={this.getFieldName(formFields.structType.name)}
                    key={this.getFieldName(formFields.structType.name)}
                    component={renderSelect}
                    isFull={true}
                    options={structTypeOptions}
                    withoutHelpWrapper={true}
                    parse={(val) => val.value}
                    isDisabled={!!isDisabledForm}
                    onChange={(e, value) => this.setState({ selectedStructType: value })}
                  />
                </div>
                <div className="cCol cCol__30 withoutLabel">{this.renderStructNumber()}</div>
                <div className="cCol cCol__30">{this.renderBuildingNumber()}</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}
